import {
  DEFAULT_DOCUMENTATION_EXTENSION,
  DocumentationGraphExtensionSchema,
  type ChecklistItem,
  type DocumentationContext,
  type DocumentationKpi,
  type DocumentationObjective,
  type DocumentationSection,
  type DocumentationSystem,
  type GeneratedDocumentation,
  type SopDocument,
  type SopStep,
} from './contracts';
import { sourceFingerprint } from './fingerprint';
import { orderDocumentationTopology } from './topology';

const MONEY = new Set([
  'investment',
  'mediaInvestment',
  'revenue',
  'projectedRevenue',
  'cpl',
  'cpa',
  'cac',
]);
const RATIO = new Set(['roi', 'roas', 'conversion']);
const KPI_ORDER = [
  'projectedClients',
  'revenue',
  'projectedRevenue',
  'leads',
  'roi',
  'roas',
  'cac',
  'cpl',
  'cpa',
  'investment',
];
const KPI_LABELS: Record<string, string> = {
  projectedClients: 'Clientes projetados',
  projectedRevenue: 'Receita projetada',
  mediaInvestment: 'Investimento em mídia',
  leads: 'Leads',
  revenue: 'Receita',
  roi: 'ROI',
  roas: 'ROAS',
  cac: 'CAC',
  cpl: 'CPL',
  cpa: 'CPA',
  investment: 'Investimento',
};

export function overviewSourceFingerprint(
  context: Pick<DocumentationContext, 'funnel' | 'nodes'>,
): string {
  const nodes = context.nodes.filter((node) => node.category !== 'documentation');
  return sourceFingerprint({
    name: context.funnel.name,
    description: context.funnel.description ?? '',
    stages: nodes.map((node) => [node.id, node.name, node.type]),
  });
}

export function sopSourceFingerprint(
  context: Pick<DocumentationContext, 'nodes' | 'edges'>,
): string {
  const nodes = context.nodes.filter((node) => node.category !== 'documentation');
  const systems = new Set([
    'crm',
    'automation',
    'webhook',
    'api',
    'chatbot',
    'email',
    'whatsapp',
    'payment',
  ]);
  return sourceFingerprint({
    nodes: nodes.map((node) => ({
      id: node.id,
      name: node.name,
      description: node.description,
      responsible: node.responsible,
      systems: node.systemName
        ? [node.systemName]
        : systems.has(node.type)
          ? [node.name]
          : undefined,
    })),
    edges: context.edges
      .filter(
        (edge) =>
          nodes.some((node) => node.id === edge.source) &&
          nodes.some((node) => node.id === edge.target),
      )
      .map((edge) => ({
        source: edge.source,
        target: edge.target,
        label: edge.label,
        condition: edge.condition,
      })),
  });
}

export function objectiveSourceFingerprint(
  context: Pick<DocumentationContext, 'funnel' | 'nodes' | 'scenarioTarget'>,
): string {
  const goalNode = context.nodes.find((node) => node.category !== 'documentation' && node.goal);
  return sourceFingerprint({
    target: context.scenarioTarget ?? goalNode?.goal,
    goalNode: goalNode?.id,
    funnel: context.funnel.name,
  });
}

function section<T>(
  id: string,
  title: string,
  content: T,
  order: number,
  source: 'derived' | 'manual' | 'ai_assisted' = 'derived',
  stale?: boolean,
): DocumentationSection<T> {
  return {
    id,
    type: id,
    title,
    content,
    source,
    order,
    visible: true,
    ...(stale ? { stale: true } : {}),
  };
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function generateFunnelDocumentation(context: DocumentationContext): GeneratedDocumentation {
  const parsedConfig = DocumentationGraphExtensionSchema.safeParse(context.config);
  const config = parsedConfig.success ? parsedConfig.data : DEFAULT_DOCUMENTATION_EXTENSION;
  const nodes = context.nodes.filter((node) => node.category !== 'documentation');
  const edges = context.edges.filter(
    (edge) =>
      nodes.some((node) => node.id === edge.source) &&
      nodes.some((node) => node.id === edge.target),
  );
  const topology = orderDocumentationTopology(nodes, edges);
  const warnings = [...topology.warnings];

  const overviewFingerprint = overviewSourceFingerprint(context);
  const derivedOverview =
    text(context.funnel.description) ??
    `${context.funnel.name} contém ${nodes.length} etapa${nodes.length === 1 ? '' : 's'} operacional${nodes.length === 1 ? '' : 'is'} e ${edges.length} transiç${edges.length === 1 ? 'ão documentada' : 'ões documentadas'}.`;
  const overviewOverride = config.overviewOverride;
  const overviewStale = Boolean(
    overviewOverride && overviewOverride.sourceFingerprint !== overviewFingerprint,
  );
  if (overviewStale)
    warnings.push({
      code: 'stale_override',
      message: 'A origem mudou desde que esta substituição foi criada.',
    });
  const overview = section(
    'overview',
    'Visão geral',
    overviewOverride?.content ?? derivedOverview,
    1,
    overviewOverride?.source ?? 'derived',
    overviewStale,
  );

  const goalNode = nodes.find((node) => node.goal);
  const target = context.scenarioTarget ?? goalNode?.goal;
  const objectiveFingerprint = objectiveSourceFingerprint(context);
  let objectiveValue: DocumentationObjective | null = config.objective ?? null;
  if (!objectiveValue && target)
    objectiveValue = {
      objective: `Atingir a meta de ${target.metric}`,
      metric: target.metric,
      target: target.value,
      ...(target.period ? { period: target.period } : {}),
      source: 'derived',
      sourceFingerprint: objectiveFingerprint,
    };
  const objectiveStale = Boolean(
    config.objective && config.objective.sourceFingerprint !== objectiveFingerprint,
  );
  if (!objectiveValue)
    warnings.push({
      code: 'missing_objective',
      message: 'Não há objetivo ou meta de cenário disponível.',
    });
  if (objectiveStale)
    warnings.push({
      code: 'stale_override',
      message: 'A origem mudou desde que esta substituição foi criada.',
    });
  const objective = section(
    'objective',
    'Objetivo',
    objectiveValue,
    2,
    objectiveValue?.source ?? 'derived',
    objectiveStale,
  );

  for (const node of nodes) {
    if (!node.description)
      warnings.push({
        code: 'missing_stage_description',
        message: `${node.name} não possui descrição da etapa.`,
        refs: [node.id],
      });
    if (!node.responsible)
      warnings.push({
        code: 'missing_responsible',
        message: `${node.name} não possui responsável ou função definida.`,
        refs: [node.id],
      });
    if (node.hasAttachments)
      warnings.push({
        code: 'attachment_not_exported',
        message: `${node.name} possui anexos referenciados, mas não incorporados às exportações.`,
        refs: [node.id],
      });
  }
  const stages = section('stages', 'Etapas', topology.ordered, 3);

  const responsibilityMap = new Map<
    string,
    { responsible: string; nodeRefs: string[]; stages: string[] }
  >();
  for (const node of nodes)
    if (node.responsible) {
      const key = node.responsible.trim().toLocaleLowerCase();
      const current = responsibilityMap.get(key) ?? {
        responsible: node.responsible.trim(),
        nodeRefs: [],
        stages: [],
      };
      current.nodeRefs.push(node.id);
      current.stages.push(node.name);
      responsibilityMap.set(key, current);
    }
  const responsibilities = section(
    'responsibilities',
    'Responsabilidades',
    [...responsibilityMap.values()].sort((a, b) => a.responsible.localeCompare(b.responsible)),
    4,
  );

  const systemTypes = new Set([
    'crm',
    'automation',
    'webhook',
    'api',
    'chatbot',
    'email',
    'whatsapp',
    'payment',
  ]);
  const systemMap = new Map<string, DocumentationSystem>();
  for (const node of nodes) {
    const explicit = text(node.systemName);
    if (!explicit && !systemTypes.has(node.type)) continue;
    const name = explicit ?? node.name;
    const key = name.toLocaleLowerCase().replace(/\s+/g, ' ');
    const existing = systemMap.get(key);
    if (existing) existing.nodeRefs.push(node.id);
    else
      systemMap.set(key, {
        id: `system-${sourceFingerprint(key)}`,
        name,
        type: node.type,
        role: node.description ?? `Dá suporte à etapa ${node.name}.`,
        nodeRefs: [node.id],
        source: 'derived',
      });
  }
  if (!systemMap.size && nodes.length)
    warnings.push({
      code: 'missing_system',
      message: 'Não há metadados explícitos de sistema ou etapa com semântica de sistema.',
    });
  const systems = section(
    'systems',
    'Sistemas',
    [...systemMap.values()].sort((a, b) => a.name.localeCompare(b.name)),
    5,
  );

  const kpiConfig = new Map((config.kpis ?? []).map((item) => [item.id, item]));
  const kpiItems: DocumentationKpi[] = [];
  for (const metric of KPI_ORDER) {
    const current = context.funnelMetrics[metric];
    const id = `funnel:${metric}`;
    const setting = kpiConfig.get(id);
    if (
      !current ||
      current.status === 'not_applicable' ||
      (current.status !== 'known' && setting?.included !== true)
    )
      continue;
    if (setting?.included === false) continue;
    kpiItems.push({
      id,
      scope: 'funnel',
      metric,
      label: setting?.label ?? KPI_LABELS[metric] ?? metric,
      current,
      ...(setting?.target !== undefined
        ? { target: setting.target }
        : target?.metric === metric
          ? { target: target.value }
          : {}),
      unit: MONEY.has(metric) ? 'money_minor' : RATIO.has(metric) ? 'ratio' : 'count',
      ...(setting?.targetPeriod
        ? { period: setting.targetPeriod }
        : target?.metric === metric && target.period
          ? { period: target.period }
          : {}),
      ...(setting?.owner ? { owner: setting.owner } : {}),
      ...(setting?.note ? { note: setting.note } : {}),
      nodeRefs: current.explanation?.contributors ? [...current.explanation.contributors] : [],
      source: 'derived',
    });
  }
  for (const node of nodes) {
    const candidates = new Set<string>();
    if (node.goal) candidates.add(node.goal.metric);
    if (node.metrics.conversion?.status === 'known') candidates.add('conversion');
    for (const metric of candidates) {
      const current = node.metrics[metric];
      if (!current || current.status === 'not_applicable') continue;
      const id = `stage:${node.id}:${metric}`;
      const setting = kpiConfig.get(id);
      if (setting?.included === false) continue;
      if (current.status !== 'known' && !node.goal && setting?.included !== true) continue;
      kpiItems.push({
        id,
        scope: 'stage',
        metric,
        label:
          setting?.label ??
          `${node.name} — ${KPI_LABELS[metric] ?? metric.replace(/([A-Z])/g, ' $1')}`,
        current,
        ...(setting?.target !== undefined
          ? { target: setting.target }
          : node.goal?.metric === metric
            ? { target: node.goal.value }
            : {}),
        unit: MONEY.has(metric) ? 'money_minor' : RATIO.has(metric) ? 'ratio' : 'count',
        ...(setting?.targetPeriod
          ? { period: setting.targetPeriod }
          : node.goal?.metric === metric && node.goal.period
            ? { period: node.goal.period }
            : {}),
        ...(setting?.owner
          ? { owner: setting.owner }
          : node.responsible
            ? { owner: node.responsible }
            : {}),
        ...(setting?.note ? { note: setting.note } : {}),
        nodeRefs: [node.id],
        source: 'derived',
      });
    }
  }
  if (!kpiItems.some((item) => item.current.status === 'known'))
    warnings.push({
      code: 'missing_kpi',
      message: 'Nenhum KPI possui dados canônicos suficientes para um valor conhecido.',
    });
  const kpis = section('kpis', 'KPIs', kpiItems, 6);

  const systemsByNode = new Map<string, string[]>();
  for (const system of systems.content)
    for (const nodeId of system.nodeRefs)
      systemsByNode.set(nodeId, [...(systemsByNode.get(nodeId) ?? []), system.name]);
  const sopBasis = {
    nodes: topology.ordered.map((node) => ({
      id: node.id,
      name: node.name,
      description: node.description,
      responsible: node.responsible,
      systems: systemsByNode.get(node.id),
    })),
    edges: edges.map((edge) => ({
      source: edge.source,
      target: edge.target,
      label: edge.label,
      condition: edge.condition,
    })),
  };
  const sopFingerprint = sopSourceFingerprint(context);
  const overlays = new Map((config.sop?.steps ?? []).map((item) => [item.nodeId, item]));
  let steps: SopStep[] = topology.ordered.map((node, index) => {
    const overlay = overlays.get(node.id);
    const outgoing = topology.outgoing.get(node.id) ?? [];
    const branch =
      outgoing.length > 1
        ? outgoing.map((edge) => edge.label ?? edge.condition ?? 'Ramificação sem rótulo').join(' / ')
        : undefined;
    const metricOutcome = Object.entries(node.metrics).find(
      ([, result]) => result.status === 'known',
    );
    const expectedOutcome =
      overlay?.expectedOutcome ??
      (metricOutcome
        ? `${metricOutcome[0]}: ${String(metricOutcome[1].effectiveValue)}`
        : undefined);
    const stale = Boolean(
      overlay &&
      overlay.sourceFingerprint !==
        sourceFingerprint(sopBasis.nodes.find((item) => item.id === node.id)),
    );
    if (stale)
      warnings.push({
        code: 'stale_override',
        message: 'A origem mudou desde que esta substituição foi criada.',
        refs: [node.id],
      });
    return {
      id: `step-${node.id}`,
      order: overlay?.order ?? index,
      title: overlay?.title ?? node.name,
      description: overlay?.description ?? node.description ?? 'Descrição não informada.',
      nodeRefs: [node.id],
      ...(node.responsible ? { responsible: node.responsible } : {}),
      systems: systemsByNode.get(node.id) ?? [],
      ...(expectedOutcome ? { expectedOutcome } : {}),
      ...(branch ? { branch } : {}),
      source: overlay?.source ?? 'derived',
      ...(stale ? { stale: true } : {}),
    };
  });
  steps = steps
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))
    .map((item, index) => ({ ...item, order: index + 1 }));
  const sopStale = Boolean(config.sop && config.sop.sourceFingerprint !== sopFingerprint);
  const sopContent: SopDocument = {
    title: config.sop?.title ?? `POP — ${context.funnel.name}`,
    purpose: config.sop?.purpose ?? 'Descreve a sequência operacional representada pelo funil.',
    prerequisites: config.sop?.prerequisites ?? [],
    steps,
    exceptions: config.sop?.exceptions ?? topology.warnings.map((warning) => warning.message),
    ...(config.sop?.escalation ? { escalation: config.sop.escalation } : {}),
    ...(config.sop?.notes ? { notes: config.sop.notes } : {}),
    source: config.sop?.source ?? 'derived',
    ...(sopStale ? { stale: true } : {}),
  };
  const sop = section('sop', 'POP', sopContent, 7, sopContent.source, sopStale);

  const savedChecklist = config.checklistItems;
  const checklistItems: ChecklistItem[] = (
    savedChecklist?.length
      ? savedChecklist.map((item) => ({
          id: item.id,
          label: item.label,
          required: item.required,
          order: item.order,
          nodeRefs: item.stepNodeId ? [item.stepNodeId] : [],
          source: 'manual' as const,
        }))
      : steps.map((step, index) => ({
          id: `check-${step.id}`,
          label: step.title,
          required: true,
          order: index + 1,
          nodeRefs: step.nodeRefs,
          source: 'derived' as const,
        }))
  ).sort((a, b) => a.order - b.order);
  const checklist = section(
    'checklist',
    'Checklist',
    checklistItems,
    8,
    savedChecklist?.length ? 'manual' : 'derived',
  );

  let attentionPoints: GeneratedDocumentation['attentionPoints'];
  if (config.includeAttentionPoints && context.diagnosis?.length) {
    attentionPoints = section('attention-points', 'Pontos de atenção', context.diagnosis, 9);
    if ((context.diagnosisCoverage ?? 1) < 0.5)
      warnings.push({
        code: 'diagnosis_low_coverage',
        message: 'A cobertura do diagnóstico é baixa; os pontos de atenção podem estar incompletos.',
      });
  }
  const sections: DocumentationSection[] = [
    overview,
    objective,
    stages,
    responsibilities,
    systems,
    kpis,
    sop,
    checklist,
    ...(attentionPoints ? [attentionPoints] : []),
  ];
  const complete = sections.filter(
    (item) => item.content !== null && (!Array.isArray(item.content) || item.content.length > 0),
  ).length;
  return {
    sections,
    overview,
    objective,
    stages,
    responsibilities,
    systems,
    kpis,
    sop,
    checklist,
    ...(attentionPoints ? { attentionPoints } : {}),
    warnings,
    coverage: {
      complete,
      total: sections.length,
      ratio: sections.length ? complete / sections.length : 0,
    },
    ...(context.funnel.ownerName ? { documentOwner: context.funnel.ownerName } : {}),
  };
}
